import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  FilterFieldOption,
  FilterValueInputRenderer,
} from '~/components/ui';
import type { SelectOption } from '~/components/ui/inputs/selectOptions';
import { FilterRowsPanel } from '~/components/ui/FilterRowsPanel';
import { multiSelectValueInput } from '~/components/ui/multiSelectValueInput';
import {
  initialValueFor,
  newRequestForQuoteFilterRow,
} from '~/pages/my-requests-for-quote/lib/requestForQuoteFilterFields';
import {
  REQUEST_FOR_QUOTE_FILTER_FIELD,
  isArchivedRow,
  type RequestForQuoteFilterRow,
} from '~/pages/my-requests-for-quote/lib/requestForQuoteFilters';

type RequestForQuoteFilterPanelProps = {
  readonly rows: RequestForQuoteFilterRow[];
  readonly fieldOptions: FilterFieldOption[];
  readonly productOptions: SelectOption[];
  readonly sellerOptions: SelectOption[];
  readonly creatorOptions: SelectOption[];
  readonly isLoadingOptions: boolean;
  readonly maxHeight?: number;
  readonly bottomContent?: ReactNode;
  readonly onRowsChange: (rows: RequestForQuoteFilterRow[]) => void;
  readonly onClearAll?: () => void;
};

export function RequestForQuoteFilterPanel({
  rows,
  fieldOptions,
  productOptions,
  sellerOptions,
  creatorOptions,
  isLoadingOptions,
  maxHeight,
  bottomContent,
  onRowsChange,
  onClearAll,
}: RequestForQuoteFilterPanelProps) {
  const { t } = useTranslation('my-requests-for-quote');

  const valueInputSource = (
    options: SelectOption[],
    placeholderKey: string,
    fieldLabelKey: string
  ) =>
    multiSelectValueInput({
      options,
      isLoading: isLoadingOptions,
      placeholder: t(`my_requests_for_quote.filter.${placeholderKey}`),
      ariaLabel: t(`my_requests_for_quote.filter.${fieldLabelKey}`),
      testIdPrefix: 'request-for-quote-filter-values',
    });

  const valueInputByField: Record<string, FilterValueInputRenderer> = {
    [REQUEST_FOR_QUOTE_FILTER_FIELD.platformProductIds]: valueInputSource(
      productOptions,
      'product_placeholder',
      'field_product'
    ),
    [REQUEST_FOR_QUOTE_FILTER_FIELD.sellingOrganizationIds]: valueInputSource(
      sellerOptions,
      'seller_placeholder',
      'field_seller'
    ),
    [REQUEST_FOR_QUOTE_FILTER_FIELD.createdByUserIds]: valueInputSource(
      creatorOptions,
      'creator_placeholder',
      'field_created_by'
    ),
  };

  return (
    <FilterRowsPanel
      namespace="my-requests-for-quote"
      keyPrefix="my_requests_for_quote.filter"
      rows={rows}
      fieldOptions={fieldOptions}
      newRow={newRequestForQuoteFilterRow}
      initialValueFor={initialValueFor}
      valueInputFor={(field) => valueInputByField[field]}
      isAlwaysAndRow={isArchivedRow}
      maxHeight={maxHeight}
      bottomContent={bottomContent}
      onRowsChange={onRowsChange}
      onClearAll={onClearAll}
    />
  );
}
